import { PropsWithChildren, useState } from 'react'
import { Box, FormControl, FormHelperText, InputLabel, styled } from '@mui/material'
import { LabelWithIndicator } from '@carto/meridian-ds/components'
import HelpTooltip from 'features/common/ui/HelpTooltip/HelpTooltip'
import CodeAreaHeader from './CodeAreaHeader'
import CodeAreaInput from './CodeAreaInput'
import CodeAreaFooter from './CodeAreaFooter'
import { CodeAreaProps } from './types'

const Root = styled(FormControl)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1
}))

const CodeMirrorWrapper = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !['variant', 'error', 'disabled', 'readOnly', 'lineNumbers', 'fullScreen'].includes(prop)
})<{
  variant?: 'filled' | 'outlined' | null
  error?: boolean
  disabled?: boolean
  readOnly?: boolean
  lineNumbers?: boolean
  fullScreen?: boolean
}>(({ variant, error, disabled, readOnly, lineNumbers, fullScreen, theme }) => ({
  position: 'relative',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: 0,
  background: theme.palette.background.paper,

  // Active line addon
  '.CodeMirror-focused .CodeMirror-activeline': {
    '& .CodeMirror-activeline-background, & .CodeMirror-activeline-gutter': {
      background: theme.palette.primary.background
    }
  },

  ...(fullScreen && {
    maxWidth: '100%',
    maxHeight: '100vh',
    height: '100vh'
  }),

  ...((variant === 'filled' || variant === 'outlined') && {
    borderRadius: theme.spacing(0.5),
    transition: theme.transitions.create('background-color', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    }),

    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      border: `1px solid ${theme.palette.default.outlinedBorder}`,
      borderRadius: theme.spacing(0.5),
      transition: theme.transitions.create('border-color', {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.shortest
      })
    },

    '&:hover:after': {
      borderColor: theme.palette.common.black
    },
    '&:focus-within': {
      overflow: 'hidden',

      '&:after': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main
      }
    },
    ...(error && {
      '&.MuiBox-root:after': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main
      }
    })
  }),
  ...(variant === 'filled' && {
    backgroundColor: theme.palette.background.paper,

    '&.codeArea-filled:after': {
      borderColor: 'transparent'
    },
    '&.codeArea-filled:hover:after': {
      borderColor: theme.palette.common.black
    },
    '&.codeArea-filled:focus-within:after': {
      borderWidth: '2px',
      borderColor: theme.palette.primary.main
    },

    '& .CodeMirror': {
      backgroundColor: theme.palette.default.background,

      '&:focus-within': {
        backgroundColor: theme.palette.background.paper
      }
    },

    ...(error && {
      borderColor: theme.palette.error.main,

      '& .CodeMirror': {
        backgroundColor: theme.palette.default.background
      },
      '&:hover, &:focus-within, &.codeArea-filled:after, &.codeArea-filled:hover:after, &.codeArea-filled:focus-within:after':
        {
          borderColor: theme.palette.error.main
        },
      '&:focus-within': {
        '& .CodeMirror': {
          backgroundColor: theme.palette.background.paper
        }
      }
    })
  }),
  ...(readOnly && {
    '& .CodeMirror': {
      backgroundColor: theme.palette.default.background,

      '.CodeMirror-cursors': {
        visibility: 'hidden !important'
      }
    },

    ...(variant === 'filled' && {
      backgroundColor: theme.palette.background.paper
    })
  }),
  ...(disabled && {
    pointerEvents: 'none',

    '& .CodeMirror': {
      color: theme.palette.text.disabled,
      backgroundColor: theme.palette.default.background,

      '& span, .CodeMirror-linenumber, .CodeMirror-guttermarker-subtle': {
        color: theme.palette.text.disabled
      }
    },

    ...(variant === 'filled' && {
      backgroundColor: theme.palette.background.paper
    })
  }),
  ...(!lineNumbers && {
    '& .CodeMirror-scroll .CodeMirror-sizer': {
      marginLeft: `${theme.spacing(1)} !important`
    },
    '& .CodeMirror-gutter-background': {
      width: `${theme.spacing(2)} !important`
    }
  })
}))

const InputWrapper = styled(Box, { shouldForwardProp: (prop: string) => prop !== 'hasActions' })<{
  hasActions?: boolean
}>(({ theme, hasActions }) => ({
  flex: '1 1 auto',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',

  ...(hasActions && {
    '.CodeMirror-line': {
      paddingRight: `${theme.spacing(4)} !important`
    }
  })
}))

export default function CodeAreaField({
  value,
  defaultValue,
  validate,
  onChange,
  onBlur,
  label,
  labelHelpTooltip,
  placeholder,
  type,
  disabled,
  copyValue,
  helperText,
  error,
  size = 'small',
  variant = 'outlined',
  onClickExpand,
  onClickFooter,
  disabledButtonFooter,
  labelIdButtonFooter,
  ariaLabelButtonFooter,
  options,
  id = 'code-area-field',
  saveLoading,
  showSaveSnackbar,
  onCloseSaveSnackbar,
  fullScreen,
  isExpanded,
  ...props
}: PropsWithChildren<CodeAreaProps>) {
  const [focused, setFocused] = useState(false)
  const readOnly = options?.readOnly ?? false
  const lineNumbers = options?.lineNumbers ?? true

  return (
    <Root size={size} error={error} disabled={disabled}>
      {label && (
        <InputLabel htmlFor={id} disabled={disabled} error={error} focused={focused} shrink>
          <LabelWithIndicator
            label={label}
            type={type}
            icon={labelHelpTooltip ? <HelpTooltip message={labelHelpTooltip} /> : undefined}
          />
        </InputLabel>
      )}
      <CodeMirrorWrapper
        variant={variant}
        error={!!error}
        disabled={disabled}
        readOnly={readOnly}
        lineNumbers={lineNumbers}
        className={variant === 'filled' ? 'codeArea-filled' : ''}
        fullScreen={fullScreen}
      >
        <CodeAreaHeader
          onClickExpand={onClickExpand}
          copyValue={copyValue}
          disabled={disabled}
          size={isExpanded ? 'medium' : 'small'}
        />
        <InputWrapper hasActions={!!copyValue || !!onClickExpand}>
          <CodeAreaInput
            {...props}
            id={id}
            disabled={disabled}
            value={value as string}
            defaultValue={defaultValue}
            placeholder={placeholder}
            onFocus={() => {
              setFocused(true)
            }}
            onBlur={(value) => {
              onBlur && onBlur(value)
              setFocused(false)
              validate && validate(value)
              onChange && onChange(value)
            }}
            onChange={onChange}
            size={size}
            options={{
              ...(readOnly && {
                styleActiveLine: false,
                matchBrackets: false
              }),
              ...options
            }}
          />
        </InputWrapper>
        <CodeAreaFooter
          labelIdButtonFooter={labelIdButtonFooter}
          ariaLabelButtonFooter={ariaLabelButtonFooter}
          onClickFooter={onClickFooter}
          disabled={disabled}
          disabledButtonFooter={disabledButtonFooter}
          size="small"
          loading={!!saveLoading}
          showSnackbar={showSaveSnackbar}
          onCloseSnackbar={onCloseSaveSnackbar}
        />
      </CodeMirrorWrapper>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </Root>
  )
}
